import React from 'react';

import { EmailSectionContainer } from './EmailSectionContainer';

interface EmailSectionProps {
  onSubmit: (email: string, password?: string, displayName?: string, country?: string, stateCode?: string) => void;
  isLoading?: boolean;
  externalError?: string;
  onErrorClear?: () => void;
}

const processEmailStep = (
  email: string,
  validateEmail: (email: string) => boolean,
  setStep: (step: 'email' | 'password') => void
) => {
  if (validateEmail(email)) {
    setStep('password');
  }
};

const processPasswordStep = (
  password: string,
  validatePassword: (password: string) => boolean,
  email: string,
  onSubmit: (email: string, password?: string) => void
) => {
  if (validatePassword(password)) {
    onSubmit(email, password);
  }
};

const processRegisterStep = (
  email: string,
  password: string,
  displayName: string,
  country: string,
  stateCode: string,
  validateEmail: (email: string) => boolean,
  onSubmit: (email: string, password?: string, displayName?: string, country?: string, stateCode?: string) => void
) => {
  if (validateEmail(email) && password.length >= 8 && displayName && country && (country !== 'IN' || stateCode)) {
    onSubmit(email, password, displayName, country, stateCode);
  }
};

const clearErrors = (setLocalError: (error: string | null) => void, onErrorClear?: () => void) => {
  setLocalError(null);

  if (onErrorClear) onErrorClear();
};

const createSubmitHandler = (
  step: 'email' | 'password',
  mode: 'login' | 'forgot' | 'register',
  email: string,
  password: string,
  displayName: string,
  country: string,
  stateCode: string,
  validateEmail: (email: string) => boolean,
  validatePassword: (password: string) => boolean,
  setStep: (step: 'email' | 'password') => void,
  onSubmit: (email: string, password?: string, displayName?: string, country?: string, stateCode?: string) => void
) => (e?: React.FormEvent) => {
  if (e) e.preventDefault();

  if (mode === 'register') {
    processRegisterStep(email, password, displayName, country, stateCode, validateEmail, onSubmit);
  } else if (step === 'email') {
    processEmailStep(email, validateEmail, setStep);
  } else {
    processPasswordStep(password, validatePassword, email, onSubmit);
  }
};

const createBackToEmailHandler = (
  setStep: (step: 'email' | 'password') => void,
  setPassword: (password: string) => void,
  setLocalError: (error: string | null) => void,
  onErrorClear?: () => void
) => () => {
  setStep('email');

  setPassword('');

  clearErrors(setLocalError, onErrorClear);
};

const createBackToLoginHandler = (
  setMode: (mode: 'login' | 'forgot') => void,
  setForgotSuccess: (success: boolean) => void,
  setLocalError: (error: string | null) => void,
  setForgotError: (error: string | null) => void,
  onErrorClear?: () => void
) => () => {
  setMode('login');

  setForgotSuccess(false);

  setLocalError(null);

  setForgotError(null);

  if (onErrorClear) onErrorClear();
};

const createSwitchModeHandler = (
  setMode: (mode: 'login' | 'forgot' | 'register') => void,
  setLocalError: (error: string | null) => void,
  onErrorClear?: () => void
) => (newMode: 'login' | 'register') => {
  setMode(newMode);
  if (setLocalError) setLocalError(null);
  if (onErrorClear) onErrorClear();
};

export const EmailSection: React.FC<EmailSectionProps> = ({
  onSubmit,
  isLoading,
  externalError,
  onErrorClear,
}) => (
  <EmailSectionContainer
    onSubmit={onSubmit}
    isLoading={isLoading}
    externalError={externalError}
    onErrorClear={onErrorClear}
    createSubmitHandler={createSubmitHandler}
    createBackToEmailHandler={createBackToEmailHandler}
    createBackToLoginHandler={createBackToLoginHandler}
    createSwitchModeHandler={createSwitchModeHandler}
  />
);
